/**
 * Chat history service — thin wrappers around the Node /chat endpoints.
 *
 * Endpoint contract (Node side, scoped to user_id from JWT):
 *
 *   POST   /chat/conversations
 *          body: { id?, company_id? }   // frontend mints id (UUID)
 *          returns: full conversation row with empty turns
 *
 *   GET    /chat/conversations
 *          returns: [{ id, title, company_ids, updated_at, last_turn_preview? }]
 *                   sorted by updated_at desc, no full turns array
 *
 *   GET    /chat/conversations/:id
 *          returns: full row including turns array
 *
 *   POST   /chat/conversations/:id/turns
 *          body: { role, content, company_id? }
 *          returns: { ...turn, created_at }
 *
 *   PATCH  /chat/conversations/:id
 *          body: { title?, append_company_id?, turns? }
 *          returns: updated row
 *
 *   DELETE /chat/conversations/:id
 *          returns: 204
 *
 * Error handling: every method throws on non-2xx. Callers should try/catch
 * and degrade gracefully — chat UI must keep working even if persistence
 * is unavailable (e.g., Node endpoints not yet shipped).
 */

import { apiRequest, apiPost, apiGet } from './apiClient';

const ROOT = 'chat/conversations';

const handle = async (resp, op) => {
    if (resp.status === 204) return null;
    if (!resp.ok) {
        let msg = `${op} failed (${resp.status})`;
        try {
            const body = await resp.json();
            if (body?.error) msg = `${op}: ${body.error}`;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
    }
    return resp.json();
};

export const createConversation = async ({ id, companyId } = {}) => {
    const resp = await apiPost(ROOT, {
        ...(id ? { id } : {}),
        ...(companyId ? { company_id: companyId } : {}),
    });
    return handle(resp, 'createConversation');
};

export const listConversations = async () => {
    const resp = await apiGet(ROOT);
    return handle(resp, 'listConversations');
};

export const getConversation = async (id) => {
    const resp = await apiGet(`${ROOT}/${id}`);
    return handle(resp, 'getConversation');
};

export const appendTurn = async (conversationId, { role, content, companyId } = {}) => {
    const resp = await apiPost(`${ROOT}/${conversationId}/turns`, {
        role,
        content,
        ...(companyId ? { company_id: companyId } : {}),
    });
    return handle(resp, 'appendTurn');
};

export const updateConversation = async (conversationId, patch) => {
    const resp = await apiRequest(`${ROOT}/${conversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
    });
    return handle(resp, 'updateConversation');
};

export const deleteConversation = async (conversationId) => {
    const resp = await apiRequest(`${ROOT}/${conversationId}`, { method: 'DELETE' });
    return handle(resp, 'deleteConversation');
};

// ─── Serializers between React `messages` shape and DB `turns` shape ───
//
// React message: { type: 'user'|'assistant'|'clarifier'|'error', ...rest }
// DB turn:       { role, content: <rest>, company_id, created_at }

export const messageToTurnContent = (msg) => {
    if (!msg || typeof msg !== 'object') return {};
    const { type, ...rest } = msg;  // eslint-disable-line no-unused-vars
    return rest;
};

export const turnToMessage = (turn) => {
    if (!turn || typeof turn !== 'object') return null;
    const content = turn.content && typeof turn.content === 'object' ? turn.content : {};
    return { type: turn.role, ...content };
};
